import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Button from '@mui/material/Button';
import {
  GridActionsCellItem,
  GridActionsCellItemProps,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid-premium';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import { Table } from '../../../../components/Table';
import { TableRef } from '../../../../components/Table/Table';
import { fetchStatuses } from '../../../../constants/fetchStatuses';
import { useAppDispatch } from '../../../../redux/hooks';
import { deleteUserNote, fetchUserNotes } from '../../../../redux/modules/user/user.actions';
import { userNotesSelector } from '../../../../redux/modules/user/user.selectors';
import { NotesTypes, UserNotes } from '../../../../types/userNotes';
import { isDispatchApiSuccess } from '../../../../utils/api';
import { UserNotesDialog } from './UserNotesDialog';
import { TableCard } from '../../../../components/Section';

interface NotesTabProps {
  userId: number;
}

function UserNotesTab({ userId }: NotesTabProps) {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [editValue, setEditValue] = useState<UserNotes | null>(null);
  const tableRef = useRef<TableRef>(null);
  const [deletingNoteId, setDeletingNoteId] = useState<number | null>(null);

  const [columns, setColumns] = useState<GridColDef[]>([
    {
      field: 'content',
      headerName: 'Content',
      width: 300,
      valueFormatter: (params) => params?.value,
    },
    {
      field: 'creator',
      headerName: 'Creator',
      width: 150,
      valueFormatter: (params) => params.value,
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 150,
      valueFormatter: (params) => NotesTypes[params.value],
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      type: 'date',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }

        return params.formattedValue;
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      type: 'date',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }

        return params.formattedValue;
      },
    },
  ]);

  const { userNotes, totalUserNotesItems, fetchUserNotesStatus, deleteStatus } = useSelector(userNotesSelector);
  const fetchDataLoading = fetchUserNotesStatus === fetchStatuses.pending;

  const onAddNew = (): void => {
    setOpen(true);
  };

  const handleClose = (isReload: boolean) => {
    if (isReload) {
      tableRef?.current?.reload();
    }

    setEditValue(null);
    setOpen(false);
  };

  const onEditUserNote = (row: UserNotes): void => {
    setEditValue(row);
    setOpen(true);
  };

  const onDeleteUserNote = async (id: number) => {
    setDeletingNoteId(id);
    const res = await dispatch(deleteUserNote(id));
    setDeletingNoteId(null);

    if (isDispatchApiSuccess(res)) {
      handleClose(true);
      enqueueSnackbar('User Note deleted!', { variant: 'success' });
    } else {
      enqueueSnackbar('User Note deletion error!', { variant: 'error' });
    }
  };
  useEffect(() => {
    const actionColumn = {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      hideable: false,
      filterable: false,
      sortable: false,
      getActions: (params: GridRowParams) => {
        const actions: React.ReactElement<GridActionsCellItemProps>[] = [];

        actions.push(
          <GridActionsCellItem
            key={`userNote-list-update-${params.row.id}`}
            icon={<EditOutlinedIcon />}
            label="Actions"
            onClick={() => onEditUserNote(params.row)}
          />,
        );

        actions.push(
          <GridActionsCellItem
            key={`userNote-list-delete-${params.row.id}`}
            icon={deletingNoteId === params.row.id ? <CircularProgress size={20} /> : <DeleteOutlineOutlinedIcon />}
            disabled={deleteStatus === fetchStatuses.pending}
            label="Link"
            onClick={() => onDeleteUserNote(params.row.id)}
          />,
        );

        return actions;
      },
    };

    setColumns([actionColumn, ...columns.filter((i) => i.type !== 'actions')]);
  }, [deleteStatus]);

  return (
    <TableCard
      title="Notes"
      description="Internal notes recorded against this user"
      action={
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={onAddNew}
          aria-label="Create Note"
          sx={{ textTransform: 'none' }}
        >
          Add note
        </Button>
      }
    >
      <UserNotesDialog userId={userId} open={open} onClose={handleClose} initialValue={editValue} />

      <Table
        ref={tableRef}
        fetchItemsCustomParams={{ userId }}
        columns={columns}
        fetchItems={fetchUserNotes}
        rows={fetchDataLoading ? [] : userNotes}
        loading={fetchDataLoading}
        rowCount={fetchDataLoading ? 0 : totalUserNotesItems}
        disableColumnFilter
        disableColumnMenu
        initialColumnVisibilityModel={{ id: false }}
        initialSortModel={[{ field: 'id', sort: 'desc' }]}
        toolbarExportProps={{
          csvOptions: { fileName: `user-${userId}-notes-${Date.now()}` },
          excelOptions: { fileName: `user-${userId}-notes-${Date.now()}` },
        }}
      />
    </TableCard>
  );
}

export default UserNotesTab;
